import { useState, useEffect } from 'react';
import { api } from './api';
import './App.css';

const DOMAINS = ['TRADING', 'CATALOG', 'AI_TOOLING', 'PKOS', 'GENERAL'];
const PRIORITIES = ['H', 'M', 'L'];
const PATTERN_TAGS = ['FOMO_ENTRY', 'EARLY_EXIT', 'RULE_SKIP', 'OVER_BATCH', 'CONTEXT_LOSS', 'INSTRUCTION_DRIFT'];

function Badge({ text, type }) {
  const colors = {
    H: '#ff4444', M: '#ff9900', L: '#44bb44',
    TRADING: '#3b82f6', CATALOG: '#8b5cf6', AI_TOOLING: '#06b6d4',
    PKOS: '#f59e0b', GENERAL: '#6b7280',
    'IN PROGRESS': '#3b82f6', 'IN DEVELOPMENT': '#f59e0b', 'BUILDING': '#8b5cf6', 'DONE': '#44bb44'
  };
  return (
    <span style={{
      background: colors[text] || '#374151',
      color: 'white', padding: '2px 8px',
      borderRadius: '4px', fontSize: '11px', fontWeight: 600
    }}>{text}</span>
  );
}

function Card({ title, children, action }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>{title}</h3>
        {action && <button className="btn-sm" onClick={action.fn}>{action.label}</button>}
      </div>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function App() {
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [alert, setAlert] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const summary = await api.summary();
      setData(summary);
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to connect to PKOS Worker. Check your token and URL.' });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const showAlert = (msg, type = 'success') => {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const submitRule = async () => {
    const res = await api.rules.add(form);
    if (res.warning === 'contradiction_check') {
      showAlert(`Contradiction detected: ${res.message}`, 'warn');
    } else {
      showAlert('Rule added');
      setModal(null);
      load();
    }
  };

  const submitDecision = async () => {
    await api.decisions.add(form);
    showAlert('Decision logged');
    setModal(null);
    load();
  };

  const submitMistake = async () => {
    const res = await api.mistakes.add(form);
    if (res.warning === 'repeated_mistake') {
      showAlert(res.message, 'warn');
    } else {
      showAlert('Mistake logged');
    }
    setModal(null);
    load();
  };

  const submitOpenItem = async () => {
    await api.openItems.add(form);
    showAlert('Open item added');
    setModal(null);
    load();
  };

  const logSession = async () => {
    await api.sessionLog.add(form);
    showAlert('Session logged');
    setModal(null);
    load();
  };

  if (loading) return (
    <div className="loading">
      <div className="spinner" />
      <p>Loading PKOS...</p>
    </div>
  );

  const tabs = ['overview', 'rules', 'decisions', 'mistakes', 'projects', 'open items', 'sessions'];

  return (
    <div className="app">
      {alert && (
        <div className={`alert alert-${alert.type}`}>{alert.msg}</div>
      )}

      <header>
        <div className="logo">PKOS</div>
        <div className="subtitle">Personal Knowledge Operating System</div>
        <button className="btn-refresh" onClick={load}>↻ Refresh</button>
      </header>

      {data.forgotten_items?.length > 0 && (
        <div className="forgotten-banner">
          ⚠ {data.forgotten_items.length} forgotten items need attention:
          {data.forgotten_items.map(i => <span key={i.id} className="forgotten-tag">{i.item}</span>)}
        </div>
      )}

      <nav>
        {tabs.map(t => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </nav>

      <main>
        {tab === 'overview' && (
          <div className="grid-2">
            <Card title="Active Projects">
              {data.projects?.map(p => (
                <div key={p.id} className="list-item">
                  <div><strong>{p.name}</strong> <Badge text={p.version} type="version" /></div>
                  <div style={{ marginTop: 4 }}><Badge text={p.status} /> <span className="muted">{p.notes?.substring(0, 60)}...</span></div>
                </div>
              ))}
            </Card>

            <Card title="Open Items" action={{ label: '+ Add', fn: () => { setForm({}); setModal('openItem'); } }}>
              {data.open_items?.slice(0, 6).map(i => (
                <div key={i.id} className="list-item">
                  <Badge text={i.priority} /> <Badge text={i.domain} /> <span style={{ marginLeft: 6 }}>{i.item}</span>
                </div>
              ))}
            </Card>

            <Card title="Recent Sessions">
              {data.recent_sessions?.map(s => (
                <div key={s.id} className="list-item">
                  <div className="muted">{s.session_date} {s.session_time} <Badge text={s.mode} /></div>
                  <div style={{ marginTop: 4 }}>{s.summary.substring(0, 100)}...</div>
                </div>
              ))}
            </Card>

            <Card title="Top Repeated Mistakes">
              {data.mistakes?.slice(0, 5).map(m => (
                <div key={m.id} className="list-item">
                  <Badge text={m.pattern_tag} /> <span className="repeated">×{m.times_repeated}</span>
                  <div className="muted" style={{ marginTop: 4 }}>{m.mistake}</div>
                </div>
              ))}
            </Card>
          </div>
        )}

        {tab === 'rules' && (
          <Card title="Rules" action={{ label: '+ Add Rule', fn: () => { setForm({}); setModal('rule'); } }}>
            {DOMAINS.map(domain => {
              const domainRules = data.rules?.filter(r => r.domain === domain);
              if (!domainRules?.length) return null;
              return (
                <div key={domain} className="domain-section">
                  <div className="domain-label"><Badge text={domain} /></div>
                  {domainRules.map(r => (
                    <div key={r.id} className="list-item">
                      <span>{r.rule}</span>
                      <span className="muted date">{r.added_date}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </Card>
        )}

        {tab === 'decisions' && (
          <Card title="Decisions Log" action={{ label: '+ Log Decision', fn: () => { setForm({}); setModal('decision'); } }}>
            {data.decisions?.map(d => (
              <div key={d.id} className="list-item">
                <div><Badge text={d.domain} /> <span className="muted date">{d.added_date} {d.added_time}</span></div>
                <div style={{ marginTop: 4 }}><strong>{d.decision}</strong></div>
                {d.reason && <div className="muted" style={{ marginTop: 2 }}>{d.reason}</div>}
              </div>
            ))}
          </Card>
        )}

        {tab === 'mistakes' && (
          <Card title="Mistakes & Pattern Tracker" action={{ label: '+ Log Mistake', fn: () => { setForm({}); setModal('mistake'); } }}>
            {data.mistakes?.map(m => (
              <div key={m.id} className="list-item">
                <div>
                  <Badge text={m.domain} />
                  <span className="pattern-tag">{m.pattern_tag}</span>
                  <span className="repeated">×{m.times_repeated}</span>
                </div>
                <div style={{ marginTop: 4 }}>{m.mistake}</div>
                <div className="muted">First: {m.first_occurrence} | Last: {m.last_occurrence}</div>
              </div>
            ))}
          </Card>
        )}

        {tab === 'projects' && (
          <Card title="Projects" action={{ label: '+ New Project', fn: () => { setForm({}); setModal('project'); } }}>
            {data.projects?.map(p => (
              <div key={p.id} className="list-item project-item">
                <div><strong>{p.name}</strong> <Badge text={p.version} /> <Badge text={p.status} /></div>
                <div className="muted" style={{ marginTop: 4 }}>{p.notes}</div>
                <div className="muted">Last updated: {p.last_updated}</div>
              </div>
            ))}
          </Card>
        )}

        {tab === 'open items' && (
          <Card title="Open Items" action={{ label: '+ Add Item', fn: () => { setForm({}); setModal('openItem'); } }}>
            {['H', 'M', 'L'].map(priority => {
              const items = data.open_items?.filter(i => i.priority === priority);
              if (!items?.length) return null;
              return (
                <div key={priority} className="domain-section">
                  <div className="domain-label"><Badge text={priority} /></div>
                  {items.map(i => (
                    <div key={i.id} className="list-item">
                      <Badge text={i.domain} /> <span style={{ marginLeft: 6 }}>{i.item}</span>
                      <span className="muted date">{i.added_date}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </Card>
        )}

        {tab === 'sessions' && (
          <Card title="Session Log" action={{ label: '+ Log Session', fn: () => { setForm({}); setModal('session'); } }}>
            {data.recent_sessions?.map(s => (
              <div key={s.id} className="list-item">
                <div><strong>{s.session_date}</strong> <span className="muted">{s.session_time}</span> <Badge text={s.mode} /></div>
                <div style={{ marginTop: 4 }}>{s.summary}</div>
              </div>
            ))}
          </Card>
        )}
      </main>

      {modal === 'rule' && (
        <Modal title="Add Rule" onClose={() => setModal(null)}>
          <select onChange={e => setForm({ ...form, domain: e.target.value })}>
            <option value="">Select Domain</option>
            {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <textarea placeholder="Rule description..." onChange={e => setForm({ ...form, rule: e.target.value })} />
          <button className="btn-primary" onClick={submitRule}>Add Rule</button>
        </Modal>
      )}

      {modal === 'decision' && (
        <Modal title="Log Decision" onClose={() => setModal(null)}>
          <select onChange={e => setForm({ ...form, domain: e.target.value })}>
            <option value="">Select Domain</option>
            {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <input placeholder="Decision..." onChange={e => setForm({ ...form, decision: e.target.value })} />
          <textarea placeholder="Reason..." onChange={e => setForm({ ...form, reason: e.target.value })} />
          <button className="btn-primary" onClick={submitDecision}>Log Decision</button>
        </Modal>
      )}

      {modal === 'mistake' && (
        <Modal title="Log Mistake" onClose={() => setModal(null)}>
          <select onChange={e => setForm({ ...form, domain: e.target.value })}>
            <option value="">Select Domain</option>
            {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <input placeholder="What happened..." onChange={e => setForm({ ...form, mistake: e.target.value })} />
          <select onChange={e => setForm({ ...form, pattern_tag: e.target.value })}>
            <option value="">Select Pattern Tag</option>
            {PATTERN_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button className="btn-primary" onClick={submitMistake}>Log Mistake</button>
        </Modal>
      )}

      {modal === 'openItem' && (
        <Modal title="Add Open Item" onClose={() => setModal(null)}>
          <select onChange={e => setForm({ ...form, domain: e.target.value })}>
            <option value="">Select Domain</option>
            {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <input placeholder="Item description..." onChange={e => setForm({ ...form, item: e.target.value })} />
          <select onChange={e => setForm({ ...form, priority: e.target.value })}>
            <option value="">Priority</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <button className="btn-primary" onClick={submitOpenItem}>Add Item</button>
        </Modal>
      )}

      {modal === 'session' && (
        <Modal title="Log Session" onClose={() => setModal(null)}>
          <select onChange={e => setForm({ ...form, mode: e.target.value })}>
            <option value="">Mode</option>
            {['Chat', 'Cowork', 'Code'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <textarea placeholder="Session summary..." rows={4} onChange={e => setForm({ ...form, summary: e.target.value })} />
          <button className="btn-primary" onClick={logSession}>Log Session</button>
        </Modal>
      )}

      {modal === 'project' && (
        <Modal title="New Project" onClose={() => setModal(null)}>
          <input placeholder="Project name..." onChange={e => setForm({ ...form, name: e.target.value })} />
          <input placeholder="Version (e.g. v1.0)..." onChange={e => setForm({ ...form, version: e.target.value })} />
          <textarea placeholder="Notes..." onChange={e => setForm({ ...form, notes: e.target.value })} />
          <button className="btn-primary" onClick={async () => {
            await api.projects.add(form);
            showAlert('Project created');
            setModal(null);
            load();
          }}>Create Project</button>
        </Modal>
      )}
    </div>
  );
}
